import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BadgeCheck, Clock, FileCheck2, FilterX, MapPin, Search, ShieldCheck, Star, Users } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { api, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SelectField } from "@/components/SelectField";

const LISTING_PAGE_SIZE = 48;
import { MONTHS, MONTH_OPTIONS } from "@/lib/months";

const META = {
  domestic: {
    kicker: "Domestic Collection",
    title: "India, in exquisite detail.",
    desc: "Palaces, backwaters, mountains and coastlines — hand-built journeys across India & the subcontinent.",
    image: "https://images.unsplash.com/photo-1661924326425-c14a6426d989?auto=format&fit=crop&w=2000&q=80",
    geoLabel: "Select State",
  },
  international: {
    kicker: "International Collection",
    title: "The world, tailored to you.",
    desc: "Bespoke holidays across 75+ countries with flights, stays and experiences stitched seamlessly together.",
    image: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=2000&q=80",
    geoLabel: "Select Continent",
  },
  visa: {
    kicker: "Visa Concierge",
    title: "Borders, opened effortlessly.",
    desc: "A 99.4% approval record across Schengen, USA, UAE and 40+ destinations — documentation handled end to end.",
    image: "https://images.unsplash.com/photo-1581553673739-c4906b5d0de8?auto=format&fit=crop&w=2000&q=80",
    geoLabel: "Select Continent",
  },
};

const Chip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-[11px] font-mono tracking-widest uppercase px-4 py-2 rounded-full border transition-all duration-200 ${
      active ? "bg-gold text-ink border-gold" : "border-cream/20 text-mist hover:border-gold/50 hover:text-gold"
    }`}
  >
    {children}
  </button>
);

export default function PackagesPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim().toLowerCase();
  const settings = useSettings();
  const meta = useMemo(() => {
    const m = META[category];
    if (!m) return m;
    if (category === "international") return { ...m, desc: `Bespoke holidays across ${settings.stat_countries}+ countries with flights, stays and experiences stitched seamlessly together.` };
    if (category === "visa") return { ...m, desc: `A ${settings.stat_visa_success} approval record across Schengen, USA, UAE and 40+ destinations — documentation handled end to end.` };
    return m;
  }, [category, settings]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState("all");
  const [tourType, setTourType] = useState("all");
  const [geo1, setGeo1] = useState("all");
  const [geo2, setGeo2] = useState("all");
  const [month, setMonth] = useState("all");
  const [visibleCount, setVisibleCount] = useState(LISTING_PAGE_SIZE);

  // restart the window whenever the result set changes
  useEffect(() => {
    setVisibleCount(LISTING_PAGE_SIZE);
  }, [theme, tourType, geo1, geo2, month, q, category]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTheme("all");
    setTourType("all");
    setGeo1("all");
    setGeo2("all");
    if (!meta) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(`/packages?category=${category}`)
      .then((res) => setPackages(res.data))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [category, meta]);

  const themeOptions = useMemo(
    () => [...new Set(packages.flatMap((p) => p.themes || []))].sort(),
    [packages]
  );
  const tourTypeOptions = useMemo(
    () => [...new Set(packages.map((p) => p.tour_type).filter(Boolean))].sort(),
    [packages]
  );
  const geo1Options = useMemo(() => {
    if (category === "domestic") return [...new Set(packages.flatMap((p) => p.states || []))].sort();
    if (category === "international" || category === "visa") return [...new Set(packages.map((p) => p.continent).filter(Boolean))].sort();
    return [];
  }, [packages, category]);
  const geo2Options = useMemo(() => {
    if (category !== "international" && category !== "visa") return [];
    const pool = geo1 === "all" ? packages : packages.filter((p) => p.continent === geo1);
    return [...new Set(pool.flatMap((p) => p.countries || []))].sort();
  }, [packages, category, geo1]);

  const filtered = useMemo(
    () =>
      packages.filter((p) => {
        if (q) {
          const hay = [p.title, p.destination, p.continent, p.tag, p.tour_type, ...(p.states || []), ...(p.countries || []), ...(p.themes || [])]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (theme !== "all" && !(p.themes || []).includes(theme)) return false;
        if (tourType !== "all" && p.tour_type !== tourType) return false;
        if (category === "domestic" && geo1 !== "all" && !(p.states || []).includes(geo1)) return false;
        if (category === "international" || category === "visa") {
          if (geo1 !== "all" && p.continent !== geo1) return false;
          if (geo2 !== "all" && !(p.countries || []).includes(geo2)) return false;
        }
        if (month !== "all" && !(p.best_months || []).includes(Number(month))) return false;
        return true;
      }),
    [packages, theme, tourType, geo1, geo2, month, category, q]
  );

  const hasFilters = theme !== "all" || tourType !== "all" || geo1 !== "all" || geo2 !== "all" || month !== "all" || !!q;
  const visiblePackages = filtered.slice(0, visibleCount);
  const clearFilters = () => {
    setTheme("all");
    setTourType("all");
    setGeo1("all");
    setGeo2("all");
    setMonth("all");
    setSearchParams({});
  };

  if (!meta) {
    return (
      <div className="bg-ink min-h-screen text-cream">
        <Navbar />
        <div className="pt-40 pb-24 text-center px-4" data-testid="packages-not-found">
          <h1 className="font-display text-3xl mb-4">Unknown collection.</h1>
          <Link to="/" className="text-gold text-sm underline underline-offset-4">Return home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-ink min-h-screen text-cream" data-testid={`packages-page-${category}`}>
      <Navbar />

      <section className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <img src={meta.image} alt={meta.kicker} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex flex-col justify-end pb-14">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-4">{meta.kicker}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream max-w-2xl">
              {meta.title}
            </h1>
            <p className="mt-4 text-base text-mist max-w-xl">{meta.desc}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          {!loading && (
            <div className="rounded-3xl border border-gold/15 bg-card/50 p-6 sm:p-8 mb-12" data-testid="filter-bar">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-7">
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-gold mb-2.5">
                    Search {category === "visa" ? "Visas" : "Packages"}
                  </label>
                  <div className="flex items-center bg-ink/60 border border-cream/15 rounded-xl px-4 focus-within:border-gold/50 transition-colors">
                    <Search size={15} className="text-gold shrink-0" />
                    <input
                      value={searchParams.get("q") || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchParams(v.trim() ? { q: v } : {});
                      }}
                      data-testid="listing-search-input"
                      className="flex-1 bg-transparent outline-none px-3 py-3 text-sm text-cream placeholder:text-faded"
                      placeholder={category === "visa" ? "Search country — Japan, France, UAE…" : "Search destination — Kashmir, Europe, Dubai…"}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-gold mb-2.5">
                    {meta.geoLabel}
                  </label>
                  <SelectField
                    value={geo1}
                    onChange={(v) => { setGeo1(v); setGeo2("all"); }}
                    testid="filter-geo1-select"
                    options={[
                      { value: "all", label: category === "domestic" ? "All States" : "All Continents" },
                      ...geo1Options.map((o) => ({ value: o, label: o })),
                    ]}
                  />
                </div>
                {(category === "international" || category === "visa") && (
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-gold mb-2.5">
                      Select Country
                    </label>
                    <SelectField
                      value={geo2}
                      onChange={setGeo2}
                      testid="filter-country-select"
                      options={[
                        { value: "all", label: "All Countries" },
                        ...geo2Options.map((o) => ({ value: o, label: o })),
                      ]}
                    />
                  </div>
                )}
                {category !== "visa" && (
                  <div>
                    <label className="block text-xs font-mono tracking-widest uppercase text-gold mb-2.5">
                      Travel Month
                    </label>
                    <SelectField
                      value={month}
                      onChange={setMonth}
                      testid="filter-month-select"
                      options={MONTH_OPTIONS}
                    />
                    {month !== "all" && (
                      <p className="text-[11px] text-faded mt-2">Tours at their best in {MONTHS[Number(month) - 1]}</p>
                    )}
                  </div>
                )}
                <div className="flex items-end">
                  {hasFilters && (
                    <button
                      onClick={clearFilters}
                      data-testid="filter-clear-btn"
                      className="flex items-center gap-2 text-xs text-mist border border-cream/20 px-5 py-3 rounded-full hover:border-gold hover:text-gold transition-colors"
                    >
                      <FilterX size={14} /> Clear filters
                    </button>
                  )}
                </div>
              </div>

              {category !== "visa" && tourTypeOptions.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-mono tracking-widest uppercase text-gold mb-3 flex items-center gap-2">
                    <Users size={13} /> Tour Type
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="filter-tour-types">
                    <Chip active={tourType === "all"} onClick={() => setTourType("all")} testid="tour-type-all">All</Chip>
                    {tourTypeOptions.map((t) => (
                      <Chip key={t} active={tourType === t} onClick={() => setTourType(t)} testid={`tour-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {t}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              {category !== "visa" && themeOptions.length > 0 && (
                <div>
                  <p className="text-xs font-mono tracking-widest uppercase text-gold mb-3">Themes</p>
                  <div className="flex flex-wrap gap-2" data-testid="filter-themes">
                    <Chip active={theme === "all"} onClick={() => setTheme("all")} testid="theme-all">All Themes</Chip>
                    {themeOptions.map((t) => (
                      <Chip key={t} active={theme === t} onClick={() => setTheme(t)} testid={`theme-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {t}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-faded" data-testid="packages-count">
                {loading
                  ? "Loading…"
                  : `${filtered.length} curated ${category === "visa" ? "service" : "journey"}${filtered.length === 1 ? "" : "s"}${hasFilters ? " matching your filters" : ""}`}
              </p>
              {q && (
                <span className="flex items-center gap-1.5 text-[11px] font-mono tracking-widest uppercase text-gold border border-gold/40 px-3 py-1.5 rounded-full" data-testid="search-query-chip">
                  <Search size={11} /> "{searchParams.get("q")}"
                </span>
              )}
            </div>
            <Link to="/" data-testid="back-home-link" className="flex items-center gap-1.5 text-xs text-mist hover:text-gold transition-colors">
              <ArrowLeft size={14} /> Home
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-[420px] rounded-2xl bg-card/60 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-cream/15 rounded-3xl" data-testid="packages-empty-state">
              <p className="font-display text-2xl text-cream mb-3">No journeys match those filters.</p>
              <p className="text-sm text-mist mb-6">Try a different theme, tour type or destination.</p>
              <button
                onClick={clearFilters}
                data-testid="empty-clear-filters-btn"
                className="text-xs font-semibold text-ink bg-gold px-6 py-3 rounded-full hover:bg-gold-hover transition-colors"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visiblePackages.map((p, i) => (
                <motion.article
                  key={p.id}
                  data-testid={`listing-card-${p.id}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                  className="group rounded-2xl overflow-hidden bg-card border border-gold/10 hover:border-gold/40 transition-colors duration-300 flex flex-col"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 bg-ink/80 backdrop-blur-sm text-gold text-[11px] font-mono tracking-widest uppercase px-3 py-1.5 rounded-full border border-gold/30">
                      {p.tag}
                    </span>
                    <span className="absolute top-4 right-4 flex items-center gap-1 bg-ink/80 backdrop-blur-sm text-cream text-xs px-3 py-1.5 rounded-full border border-cream/15">
                      <Star size={12} className="text-gold fill-gold" /> {p.rating}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display text-xl sm:text-2xl text-cream mb-2">
                      <Link to={`/package/${p.id}`} data-testid={`listing-title-link-${p.id}`} className="hover:text-gold transition-colors">
                        {p.title}
                      </Link>
                    </h3>
                    <p className="flex items-center gap-1.5 text-xs text-mist mb-1.5">
                      <MapPin size={13} className="text-gold" /> {p.destination}
                    </p>
                    <p className="flex items-center gap-1.5 text-xs text-mist mb-4">
                      <Clock size={13} className="text-gold" /> {p.duration}
                    </p>
                    <ul className="space-y-1.5 mb-3">
                      {p.highlights.slice(0, 3).map((h) => (
                        <li key={h} className="text-xs text-faded flex items-start gap-2">
                          <span className="text-gold mt-0.5">◆</span> {h}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={`/package/${p.id}`}
                      data-testid={`listing-view-details-${p.id}`}
                      className="text-xs text-gold/80 hover:text-gold inline-flex items-center gap-1 mb-4 transition-colors"
                    >
                      View full package <ArrowRight size={12} />
                    </Link>
                    <div className="mt-auto flex items-center justify-between pt-4 border-t border-cream/10">
                      <div>
                        <p className="text-[11px] uppercase tracking-widest text-faded">
                          {p.category === "visa" ? "Service fee" : "From"}
                        </p>
                        <p className="font-display text-xl text-gold">{formatINR(p.price)}</p>
                      </div>
                      <button
                        onClick={() => navigate("/", { state: { scrollTo: "enquiry", service: p.category } })}
                        data-testid={`listing-enquire-btn-${p.id}`}
                        className="group/btn flex items-center gap-1.5 text-xs font-semibold text-ink bg-gold px-5 py-2.5 rounded-full hover:bg-gold-hover transition-colors duration-200"
                      >
                        Enquire
                        <ArrowRight size={13} className="group-hover/btn:translate-x-0.5 transition-transform" />
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="mt-12 flex flex-col items-center gap-4" data-testid="listing-pagination">
              <p className="text-xs font-mono tracking-widest uppercase text-faded" data-testid="showing-count">
                Showing {Math.min(visibleCount, filtered.length)} of {filtered.length}
              </p>
              {filtered.length > visibleCount && (
                <button
                  onClick={() => setVisibleCount((n) => n + LISTING_PAGE_SIZE)}
                  data-testid="load-more-btn"
                  className="text-xs font-mono tracking-widest uppercase text-gold border border-gold/40 px-8 py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors"
                >
                  Load more ({filtered.length - visibleCount} remaining)
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {category === "visa" && !loading && (
        <section className="pb-20 px-4 sm:px-8 lg:px-16" data-testid="visa-info-section">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-3xl border border-gold/20 bg-card/50 p-8">
              <p className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] uppercase text-gold mb-6">
                <FileCheck2 size={15} /> Documents you'll generally need
              </p>
              <ul className="space-y-3">
                {[
                  "Passport valid for at least 6 months beyond travel dates",
                  "Recent passport-size photographs as per embassy specs",
                  "Bank statements for the last 6 months",
                  "Confirmed return flight & hotel bookings",
                  "Employment / business proof and ITR where applicable",
                  "Travel insurance (mandatory for Schengen — min €30,000 cover)",
                ].map((d) => (
                  <li key={d} className="text-sm text-mist flex items-start gap-2.5">
                    <BadgeCheck size={15} className="text-gold shrink-0 mt-0.5" /> {d}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-cream/10 bg-card/50 p-8">
              <p className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] uppercase text-gold mb-6">
                <ShieldCheck size={15} /> Good to know
              </p>
              <ul className="space-y-3 text-sm text-mist leading-relaxed">
                <li className="flex items-start gap-2.5"><span className="text-gold mt-1.5 text-[10px]">◆</span> Visa approval is always at the embassy's discretion — our job is a flawless, honest application.</li>
                <li className="flex items-start gap-2.5"><span className="text-gold mt-1.5 text-[10px]">◆</span> Processing times are indicative; apply 4–6 weeks before travel for sticker visas.</li>
                <li className="flex items-start gap-2.5"><span className="text-gold mt-1.5 text-[10px]">◆</span> Embassy / VFS fees are payable at actuals and are non-refundable once filed.</li>
                <li className="flex items-start gap-2.5"><span className="text-gold mt-1.5 text-[10px]">◆</span> Thailand currently allows Indians visa-free entry for up to 60 days — only the free TDAC arrival card is needed, and we file it for you.</li>
                <li className="flex items-start gap-2.5"><span className="text-gold mt-1.5 text-[10px]">◆</span> UK, Ireland, Turkey, Russia and China are NOT covered by a Schengen visa — they require separate visas.</li>
              </ul>
            </div>
          </div>
        </section>
      )}

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
